<template>
  <section>
    <div class="guests-page__head" style="margin: 4px 0 6px">
      <div>
        <h2 class="guests-page__title" style="font-size: 18px">
          Documenti retta
        </h2>
        <p class="guests-page__subtitle">
          Genera in blocco le ricevute/proforma della retta per gli ospiti della
          struttura.
        </p>
      </div>
    </div>

    <!-- Selettori + anteprima -->
    <div class="guest-detail billing-bar" style="margin-bottom: 14px">
      <div class="billing-bar__controls">
        <select v-model.number="month" class="search-box">
          <option v-for="(m, i) in monthNames" :key="i" :value="i + 1">
            {{ m }}
          </option>
        </select>
        <select v-model.number="year" class="search-box">
          <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
        </select>
        <label class="billing-skip">
          <input v-model="skipExisting" type="checkbox" />
          Salta chi ha già il documento
        </label>
        <select
          v-if="bankAccounts.length"
          v-model.number="selectedBankAccountId"
          class="search-box"
          title="Conto bancario"
        >
          <option v-for="a in bankAccounts" :key="a.id" :value="a.id">
            {{ a.label }}{{ a.isDefault ? " (default)" : "" }}
          </option>
        </select>
        <button
          class="villa-btn villa-btn--ghost"
          :disabled="loadingPreview"
          @click="loadPreview"
        >
          <i
            :class="
              loadingPreview
                ? 'fa-solid fa-spinner fa-spin'
                : 'fa-solid fa-list-check'
            "
          ></i>
          Anteprima documenti
        </button>
      </div>
    </div>

    <div v-if="preview">
      <!-- Riepilogo anteprima -->
      <div class="tpl-info" style="margin-bottom: 12px">
        <i class="fa-solid fa-circle-info"></i>
        Generabili: <strong>{{ preview.generableCount }}</strong> su
        {{ preview.totalGuests }} ospiti · Totale stimato
        <strong>{{ euro(preview.totalAmount) }}</strong> · Prossimo n°
        <strong
          >{{ pad3(preview.nextNumber) }}/{{ preview.year }}/{{
            preview.facilityCode
          }}</strong
        >
      </div>

      <div class="guests-table-wrap">
        <table class="guests-table">
          <thead>
            <tr>
              <th>Ospite</th>
              <th>Retta</th>
              <th>Stato dati</th>
              <th>Già presente</th>
              <th>Esito previsto</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="it in preview.items" :key="it.guestId">
              <td>{{ it.lastName }} {{ it.firstName }}</td>
              <td>{{ it.monthlyFee != null ? euro(it.monthlyFee) : "—" }}</td>
              <td>
                <span v-if="it.processable" class="tpl-badge tpl-badge--active">
                  OK
                </span>
                <span v-else class="bed-row__occupant">
                  {{ it.reasons.join(", ") }}
                </span>
              </td>
              <td>
                <template v-if="it.alreadyExists">
                  Sì <small>({{ it.existingDocumentNumber || "n/d" }})</small>
                </template>
                <template v-else>No</template>
              </td>
              <td>{{ outcome(it) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="form-actions" style="margin-top: 16px">
        <button
          class="villa-btn villa-btn--primary"
          :disabled="generating || preview.generableCount === 0"
          @click="confirmGenerate"
        >
          <i
            :class="
              generating
                ? 'fa-solid fa-spinner fa-spin'
                : 'fa-solid fa-file-circle-check'
            "
          ></i>
          {{ generating ? "Generazione…" : "Conferma generazione documenti" }}
        </button>
      </div>
    </div>

    <div v-else-if="!loadingPreview" class="guests-loading">
      Seleziona mese/anno e premi “Anteprima documenti”.
    </div>

    <!-- Riepilogo finale generazione -->
    <div v-if="result" class="guest-detail" style="margin-top: 16px">
      <h3 class="detail-block-title">Riepilogo generazione</h3>
      <div class="dash-insights">
        <div class="dash-card">
          <div class="dash-card__value">{{ result.generatedCount }}</div>
          <div class="dash-card__label">Generati</div>
        </div>
        <div class="dash-card">
          <div class="dash-card__value">{{ result.skippedCount }}</div>
          <div class="dash-card__label">Saltati</div>
        </div>
        <div class="dash-card">
          <div class="dash-card__value">{{ result.errorCount }}</div>
          <div class="dash-card__label">Errori</div>
        </div>
        <div class="dash-card">
          <div class="dash-card__value">{{ euro(result.totalAmount) }}</div>
          <div class="dash-card__label">Totale generato</div>
        </div>
      </div>

      <div
        v-if="result.skipped.length || result.errors.length"
        style="margin-top: 12px"
      >
        <p
          v-for="s in result.skipped"
          :key="'s' + s.guestId"
          class="bed-row__occupant"
        >
          • Saltato: {{ s.name }} — {{ s.reason }}
        </p>
        <p
          v-for="e in result.errors"
          :key="'e' + e.guestId"
          class="bed-row__occupant"
          style="color: #b91c1c"
        >
          • Errore: {{ e.name }} — {{ e.error }}
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions } from "vuex";

const MONTH_NAMES = [
  "Gennaio",
  "Febbraio",
  "Marzo",
  "Aprile",
  "Maggio",
  "Giugno",
  "Luglio",
  "Agosto",
  "Settembre",
  "Ottobre",
  "Novembre",
  "Dicembre",
];

export default {
  name: "FacilityBillingPanel",
  props: {
    facilityId: { type: [String, Number], required: true },
  },
  emits: ["changed"],
  data() {
    const now = new Date();
    return {
      monthNames: MONTH_NAMES,
      month: now.getMonth() + 1,
      year: now.getFullYear(),
      skipExisting: true,
      preview: null,
      result: null,
      loadingPreview: false,
      generating: false,
      bankAccounts: [],
      selectedBankAccountId: null,
    };
  },
  computed: {
    yearOptions() {
      const y = new Date().getFullYear();
      return [y - 1, y, y + 1];
    },
  },
  methods: {
    ...mapActions([
      "fetchFacilityBillingPreview",
      "generateFacilityBillingBulk",
      "fetchBankAccounts",
    ]),
    async loadBankAccounts() {
      try {
        this.bankAccounts = await this.fetchBankAccounts({ activeOnly: true });
        const def =
          this.bankAccounts.find((a) => a.isDefault) || this.bankAccounts[0];
        this.selectedBankAccountId = def ? def.id : null;
      } catch {
        this.bankAccounts = [];
      }
    },
    euro(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return `€ ${n.toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    pad3(n) {
      return String(n || 0).padStart(3, "0");
    },
    outcome(it) {
      if (!it.processable) return "Saltato (dati incompleti)";
      if (it.alreadyExists && this.skipExisting)
        return "Saltato (già presente)";
      return "Genera";
    },
    async loadPreview() {
      this.loadingPreview = true;
      this.result = null;
      try {
        this.preview = await this.fetchFacilityBillingPreview({
          facilityId: this.facilityId,
          month: this.month,
          year: this.year,
        });
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Anteprima non riuscita."
        );
      } finally {
        this.loadingPreview = false;
      }
    },
    async confirmGenerate() {
      this.generating = true;
      try {
        this.result = await this.generateFacilityBillingBulk({
          facilityId: this.facilityId,
          month: this.month,
          year: this.year,
          skipExisting: this.skipExisting,
          bankAccountId: this.selectedBankAccountId || undefined,
        });
        this.$toast.success(
          `Generati ${this.result.generatedCount} documenti, saltati ${this.result.skippedCount}, errori ${this.result.errorCount}.`
        );
        this.$emit("changed");
        // Aggiorna l'anteprima per riflettere i documenti appena creati.
        await this.loadPreview();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Generazione non riuscita."
        );
      } finally {
        this.generating = false;
      }
    },
  },
  mounted() {
    this.loadBankAccounts();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/RoomsPage.css" />
